import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TRIP_PURPOSE_LABELS, TRIP_PURPOSES, type CreateTripInput, type Trip, type TripCreated, type TripPurpose } from '@kontor/shared';
import { api } from '../../api';
import { ContactPicker, type PickedContact } from '../../components/ContactPicker';
import { useSession } from '../../session';
import { errorText, today, useEmployees, useVehicles } from './shared';

/**
 * Neue Fahrt im Fahrtenbuch (KON-167). Der Kilometerstand zu Beginn wird mit dem aktuellen Stand des Fahrzeugs vorbelegt, damit das Fahrtenbuch lückenlos bleibt. Bei einer
 * Geschäftsfahrt gehören der Reiseanlass oder der Geschäftspartner dazu. Der Server meldet Kilometer, die zwischen zwei Fahrten nicht erfasst sind.
 */
export function TripForm({ vehicleId, onDone, onCancel }: { vehicleId?: string; onDone: (created: TripCreated) => void; onCancel: () => void }) {
  const client = useQueryClient();
  const { hasModule } = useSession();
  const vehicles = useVehicles();
  const employees = useEmployees();
  const [vehicle, setVehicle] = useState(vehicleId ?? '');
  const [date, setDate] = useState(today());
  const [startKm, setStartKm] = useState('');
  const [endKm, setEndKm] = useState('');
  const [fromPlace, setFromPlace] = useState('');
  const [toPlace, setToPlace] = useState('');
  const [purpose, setPurpose] = useState<TripPurpose>('business');
  const [contact, setContact] = useState<PickedContact | null>(null);
  const [reason, setReason] = useState('');
  const [driver, setDriver] = useState('');
  const [error, setError] = useState<string | null>(null);

  const chosen = vehicles.data?.find((v) => v.id === vehicle);
  // Stand zu Beginn vorbelegen, sobald ein Fahrzeug feststeht
  useEffect(() => {
    if (chosen) setStartKm(String(chosen.currentKm));
  }, [chosen?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      const start = Number(startKm.replace(/\./g, ''));
      const end = Number(endKm.replace(/\./g, ''));
      if (!vehicle) throw new Error('Bitte wählen Sie ein Fahrzeug.');
      if (!Number.isInteger(start) || !Number.isInteger(end)) throw new Error('Bitte geben Sie die Kilometerstände als ganze Zahlen an.');
      const body: CreateTripInput = { vehicleId: vehicle, driverEmployeeId: driver || null, tripDate: date, startKm: start, endKm: end, fromPlace: fromPlace.trim(), toPlace: toPlace.trim(), purpose, contactId: contact?.id ?? null, reason: reason.trim() || null, note: null };
      return (await api<{ data: TripCreated }>('POST', '/fleet/trips', body)).data;
    },
    onSuccess: (created) => {
      for (const key of ['fleet-trips', 'fleet-vehicles', 'fleet-vehicle', 'fleet-overview']) client.invalidateQueries({ queryKey: [key] });
      onDone(created);
    },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Die Fahrt konnte nicht eingetragen werden.')),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };
  const distance = Number(endKm.replace(/\./g, '')) - Number(startKm.replace(/\./g, ''));

  return (
    <form className="card fleet-form" onSubmit={submit} aria-label="Neue Fahrt">
      <h2>Neue Fahrt</h2>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        {!vehicleId && (
          <label className="field">
            <span>Fahrzeug</span>
            <select className="input" value={vehicle} onChange={(e) => setVehicle(e.target.value)} required>
              <option value="">Bitte wählen</option>
              {vehicles.data?.filter((v) => v.status === 'active' || v.status === 'workshop').map((v) => <option key={v.id} value={v.id}>{v.plate}{v.name ? ` (${v.name})` : ''}</option>)}
            </select>
          </label>
        )}
        <label className="field"><span>Datum</span><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
        <label className="field"><span>Kilometerstand Beginn</span><input className="input" inputMode="numeric" value={startKm} onChange={(e) => setStartKm(e.target.value)} required /></label>
        <label className="field">
          <span>Kilometerstand Ende</span>
          <input className="input" inputMode="numeric" value={endKm} onChange={(e) => setEndKm(e.target.value)} required />
          {Number.isFinite(distance) && distance > 0 && endKm && <small className="muted">Strecke {distance.toLocaleString('de-DE')} km</small>}
        </label>
        <label className="field"><span>Von</span><input className="input" value={fromPlace} onChange={(e) => setFromPlace(e.target.value)} maxLength={150} required placeholder="München, Büro" /></label>
        <label className="field"><span>Nach</span><input className="input" value={toPlace} onChange={(e) => setToPlace(e.target.value)} maxLength={150} required placeholder="Augsburg, Kunde" /></label>
        <label className="field">
          <span>Zweck</span>
          <select className="input" value={purpose} onChange={(e) => setPurpose(e.target.value as TripPurpose)}>{TRIP_PURPOSES.map((p) => <option key={p} value={p}>{TRIP_PURPOSE_LABELS[p]}</option>)}</select>
        </label>
        {employees.data && (
          <label className="field">
            <span>Fahrer (leer: Standardfahrer)</span>
            <select className="input" value={driver} onChange={(e) => setDriver(e.target.value)}>
              <option value="">Standardfahrer</option>
              {employees.data.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
            </select>
          </label>
        )}
        {purpose === 'business' && (
          <>
            <label className="field field--wide"><span>Reiseanlass</span><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Kundentermin, Lieferung …" /></label>
            {hasModule('contacts') && (
              <div className="field field--wide">
                <span className="field__label">Geschäftspartner (aufgesucht)</span>
                <ContactPicker value={contact} onChange={setContact} label="Geschäftspartner" />
              </div>
            )}
          </>
        )}
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird eingetragen …' : 'Fahrt eintragen'}</button>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}

export type { Trip };
